import React, { useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Grid,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { formatTooltipItem } from "./chartUtils";
import { useFuelEfficiency } from "../hooks/useFuelEconomyHooks";
import type { FuelEconomyHookParams } from "../hooks/useFuelEconomyHooks";
import { useProLogsTheme } from "../../../common/hooks/useProLogsTheme";

import ReactECharts from "echarts-for-react";
import "./FuelEconomyReport.css";

type Props = FuelEconomyHookParams;

const formatNumber = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) {
    return "-";
  }
  return value.toFixed(2);
};

// Colors for time segments
const timeColors = {
  morning: "#a855f7", // Purple
  noon: "#ec4899", // Pink
  afternoon: "#3b82f6", // Blue
  night: "#f97316", // Orange
};

export const AverageFuelConsumptionEfficiencyWidget: React.FC<Props> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const theme = useProLogsTheme();
  const { data, loading: isLoading, error } = useFuelEfficiency({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;

  // Calculate overall average
  const overallAverage = useMemo(() => {
    if (!data) return null;

    let total = 0;
    let count = 0;

    const addValues = (efficiency: any) => {
      if (efficiency) {
        if (efficiency.morningValues != null) {
          total += efficiency.morningValues;
          count++;
        }
        if (efficiency.noonValues != null) {
          total += efficiency.noonValues;
          count++;
        }
        if (efficiency.afterNoonValues != null) {
          total += efficiency.afterNoonValues;
          count++;
        }
        if (efficiency.nightValues != null) {
          total += efficiency.nightValues;
          count++;
        }
      }
    };

    addValues(data.speedingConsumptionEfficiency);
    addValues(data.normalDriveConsumptionEfficiency);
    addValues(data.slowPacedConsumptionEfficiency);

    return count > 0 ? total / count : null;
  }, [data]);

  // Create chart option for each category
  const createChartOption = useCallback(
    (
      efficiency: any,
      title: string,
      centerValue: number | null,
      textColor: string,
      secondaryTextColor: string,
    ) => {
      if (!efficiency) {
        return {
          graphic: {
            elements: [
              {
                type: "text",
                left: "center",
                top: "center",
                style: {
                  text: formatNumber(centerValue),
                  fontSize: 24,
                  fontWeight: "bold",
                  fill: textColor,
                },
              },
              {
                type: "text",
                left: "center",
                top: "center",
                style: {
                  text: title,
                  fontSize: 14,
                  y: 30,
                  fill: secondaryTextColor,
                },
              },
            ],
          },
        };
      }

      const morningValue = efficiency.morningValues ?? 0;
      const noonValue = efficiency.noonValues ?? 0;
      const afternoonValue = efficiency.afterNoonValues ?? 0;
      const nightValue = efficiency.nightValues ?? 0;

      return {
        tooltip: {
          trigger: "item",
          formatter: formatTooltipItem,
        },
        graphic: [
          {
            type: "text",
            left: "center",
            top: "45%",
            style: {
              text: formatNumber(centerValue),
              fontSize: 20,
              fontWeight: "bold",
              fill: textColor,
            },
            z: 100,
          },
          {
            type: "text",
            left: "center",
            top: "60%",
            style: {
              text: title,
              fontSize: 12,
              fill: secondaryTextColor,
            },
            z: 100,
          },
        ],
        series: [
          {
            type: "pie",
            radius: ["60%", "75%"],
            center: ["50%", "50%"],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 0,
              borderColor: "#fff",
              borderWidth: 2,
            },
            label: {
              show: false,
            },
            emphasis: {
              label: {
                show: false,
              },
            },
            data: [
              {
                value: morningValue,
                name: translate("fuelEconomyReport.morning", { _: "Morning" }),
                itemStyle: { color: timeColors.morning },
              },
              {
                value: noonValue,
                name: translate("fuelEconomyReport.noon", { _: "Noon" }),
                itemStyle: { color: timeColors.noon },
              },
              {
                value: afternoonValue,
                name: translate("fuelEconomyReport.afternoon", {
                  _: "Afternoon",
                }),
                itemStyle: { color: timeColors.afternoon },
              },
              {
                value: nightValue,
                name: translate("fuelEconomyReport.night", { _: "Night" }),
                itemStyle: { color: timeColors.night },
              },
            ],
          },
        ],
      };
    },
    [translate],
  );

  const speedingValue = useMemo(() => {
    const eff = data?.speedingConsumptionEfficiency;
    if (!eff) return null;
    const values = [
      eff.morningValues,
      eff.noonValues,
      eff.afterNoonValues,
      eff.nightValues,
    ].filter((v) => v != null) as number[];
    if (values.length === 0) return null;
    return values.reduce((a, b) => a + b, 0) / values.length;
  }, [data]);

  const normalValue = useMemo(() => {
    const eff = data?.normalDriveConsumptionEfficiency;
    if (!eff) return null;
    const values = [
      eff.morningValues,
      eff.noonValues,
      eff.afterNoonValues,
      eff.nightValues,
    ].filter((v) => v != null) as number[];
    if (values.length === 0) return null;
    return values.reduce((a, b) => a + b, 0) / values.length;
  }, [data]);

  const slowPacedValue = useMemo(() => {
    const eff = data?.slowPacedConsumptionEfficiency;
    if (!eff) return null;
    const values = [
      eff.morningValues,
      eff.noonValues,
      eff.afterNoonValues,
      eff.nightValues,
    ].filter((v) => v != null) as number[];
    if (values.length === 0) return null;
    return values.reduce((a, b) => a + b, 0) / values.length;
  }, [data]);

  const speedingOption = useMemo(
    () =>
      createChartOption(
        data?.speedingConsumptionEfficiency,
        translate("fuelEconomyReport.speeding", { _: "Speeding" }),
        speedingValue,
        theme.palette.text.primary,
        theme.palette.text.secondary,
      ),
    [
      data,
      speedingValue,
      translate,
      createChartOption,
      theme.palette.text.primary,
      theme.palette.text.secondary,
    ],
  );

  const normalOption = useMemo(
    () =>
      createChartOption(
        data?.normalDriveConsumptionEfficiency,
        translate("fuelEconomyReport.normal", { _: "Normal" }),
        normalValue,
        theme.palette.text.primary,
        theme.palette.text.secondary,
      ),
    [
      data,
      normalValue,
      translate,
      createChartOption,
      theme.palette.text.primary,
      theme.palette.text.secondary,
    ],
  );

  const slowPacedOption = useMemo(
    () =>
      createChartOption(
        data?.slowPacedConsumptionEfficiency,
        translate("fuelEconomyReport.slowPaced", { _: "Slow paced" }),
        slowPacedValue,
        theme.palette.text.primary,
        theme.palette.text.secondary,
      ),
    [
      data,
      slowPacedValue,
      translate,
      createChartOption,
      theme.palette.text.primary,
      theme.palette.text.secondary,
    ],
  );

  const hasData = useMemo(() => {
    if (!data) return false;
    return !!(
      data.speedingConsumptionEfficiency ||
      data.normalDriveConsumptionEfficiency ||
      data.slowPacedConsumptionEfficiency
    );
  }, [data]);

  return (
    <Card className="widget">
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("fuelEconomyReport.averageFuelConsumptionEfficiency", {
            _: "Average fuel consumption efficiency",
          })}
        </div>
      </div>
      <CardContent style={{ padding: 16 }}>
        {overallAverage != null && (
          <Box mb={2}>
            <Typography variant="h4" style={{ fontWeight: "bold" }}>
              {formatNumber(overallAverage)}{" "}
              {translate("common.mpg", { _: "mpg" })}
            </Typography>
          </Box>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !hasData ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <ReactECharts style={{ height: 250 }} option={speedingOption} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ReactECharts style={{ height: 250 }} option={normalOption} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ReactECharts
                  style={{ height: 250 }}
                  option={slowPacedOption}
                />
              </Grid>
            </Grid>

            {/* Legend */}
            <Box mt={2} display="flex" justifyContent="center" flexWrap="wrap">
              <Box display="flex" alignItems="center" mr={2} mb={1}>
                <Box
                  width={12}
                  height={12}
                  borderRadius="50%"
                  bgcolor={timeColors.morning}
                  mr={0.5}
                />
                <Typography variant="body2">
                  {translate("fuelEconomyReport.morning", { _: "Morning" })}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mr={2} mb={1}>
                <Box
                  width={12}
                  height={12}
                  borderRadius="50%"
                  bgcolor={timeColors.noon}
                  mr={0.5}
                />
                <Typography variant="body2">
                  {translate("fuelEconomyReport.noon", { _: "Noon" })}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mr={2} mb={1}>
                <Box
                  width={12}
                  height={12}
                  borderRadius="50%"
                  bgcolor={timeColors.afternoon}
                  mr={0.5}
                />
                <Typography variant="body2">
                  {translate("fuelEconomyReport.afternoon", { _: "Afternoon" })}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={1}>
                <Box
                  width={12}
                  height={12}
                  borderRadius="50%"
                  bgcolor={timeColors.night}
                  mr={0.5}
                />
                <Typography variant="body2">
                  {translate("fuelEconomyReport.night", { _: "Night" })}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AverageFuelConsumptionEfficiencyWidget;
